export type StringDevtoolsOverlayId = string;

const DEVTOOLS_OVERLAY_BASE_OFFSET_X = 12;
const DEVTOOLS_OVERLAY_BASE_OFFSET_X_WITH_RULERS = 24;
const DEVTOOLS_OVERLAY_BASE_OFFSET_Y = 12;
const DEVTOOLS_OVERLAY_SLOT_STEP = 36;
const DEVTOOLS_OVERLAY_COLLISION_STEP = 36;
const DEVTOOLS_OVERLAY_COLLISION_SIZE = 28;

export const STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT =
  "string-devtools-overlay-layout:change";

// ── In-memory overlay registry ──────────────────────────────
// Each overlay module registers itself here when enabled and
// unregisters when disabled.  No DOM scanning needed.

const activeOverlayRegistry = new Map<string, number>();

export function registerActiveOverlay(id: string, order: number): void {
  activeOverlayRegistry.set(id, order);
}

export function unregisterActiveOverlay(id: string): void {
  activeOverlayRegistry.delete(id);
}

export function getActiveOverlays(): Array<{ id: string; order: number }> {
  const list: Array<{ id: string; order: number }> = [];
  for (const [id, order] of activeOverlayRegistry) {
    list.push({ id, order });
  }
  list.sort((a, b) => {
    if (a.order !== b.order) return a.order - b.order;
    return a.id.localeCompare(b.id);
  });
  return list;
}

// ─────────────────────────────────────────────────────────────

function areRulersVisible(): boolean {
  const rulersLayer = document.querySelector(`[data-string-dev-viewport-layer="rulers"]`);
  return Boolean(rulersLayer) && !document.body.hasAttribute("data-string-rulers-hidden");
}

export function resolveDevtoolsOverlayAnchor(
  overlayId: StringDevtoolsOverlayId,
  contentX: number,
  contentY: number,
): { docX: number; docY: number } {
  const sorted = getActiveOverlays();
  const slotIndex = sorted.findIndex(o => o.id === overlayId);
  const resolvedSlot = slotIndex >= 0 ? slotIndex : 0;
  const baseOffsetX = areRulersVisible()
    ? DEVTOOLS_OVERLAY_BASE_OFFSET_X_WITH_RULERS
    : DEVTOOLS_OVERLAY_BASE_OFFSET_X;

  return {
    docX: contentX + baseOffsetX + resolvedSlot * DEVTOOLS_OVERLAY_SLOT_STEP,
    docY: contentY + DEVTOOLS_OVERLAY_BASE_OFFSET_Y,
  };
}

export function emitDevtoolsOverlayLayoutChange(): void {
  window.dispatchEvent(new CustomEvent(STRING_DEVTOOLS_OVERLAY_LAYOUT_CHANGE_EVENT));
}

export function resolveDevtoolsCollisionOffset(
  element: HTMLElement,
  baseDocX: number,
  baseDocY: number,
  resolveAncestorAnchor: (ancestorId: string) => { docX: number; docY: number } | null,
): number {
  let offset = 0;
  let current = element.parentElement;

  while (current) {
    const ancestorId = current.getAttribute("string-id") ?? current.getAttribute("data-string-id");
    if (ancestorId) {
      const anchor = resolveAncestorAnchor(ancestorId);
      if (
        anchor &&
        Math.abs(anchor.docX - baseDocX) < DEVTOOLS_OVERLAY_COLLISION_SIZE &&
        Math.abs(anchor.docY - (baseDocY + offset)) < DEVTOOLS_OVERLAY_COLLISION_SIZE
      ) {
        offset += DEVTOOLS_OVERLAY_COLLISION_STEP;
      }
    }
    current = current.parentElement;
  }

  return offset;
}
